import { ApiClientError, getAccessToken } from '../api/client';
import type { ReceiveResult } from '../api/endpoints';
import { isOthers, type QueuedScan, type StationStore } from '../helperStations/store';
import { isSystemFailure } from '../helperStations/sync';
import type { FlushOptions, PersonSource, ScanGuard } from '../helperStations/sync';
import { messageFor } from '../api/messages';
import { addRejected } from './localIntake';

export interface IntakeScanApi {
  intakeScan(eventId: string, code: string, guard?: ScanGuard): Promise<ReceiveResult>;
}

export interface IntakeFlushOptions extends FlushOptions {
  /** Ergebnis eines gesendeten Scans (z. B. zum Auffrischen der Liste und für „Schon erfasst“). */
  onResult?: (scan: QueuedScan, result: ReceiveResult) => void;
}

export interface IntakeFlushSummary {
  sent: number;
  /** Davon schon vorher erfasst („Schon erfasst“): Der Server hat nichts geändert. */
  alreadyReceived: number;
  /** Vom Server für genau diesen Scan abgelehnt (Hinweis bleibt gespeichert, siehe `listRejected`). */
  rejected: number;
  remaining: number;
  /**
   * Weshalb der Lauf abbrach, obwohl noch Scans warten (kein Netz, Sitzung abgelaufen, 403, Annahme nicht geöffnet, 429, Serverfehler):
   * Die Scans bleiben unverändert liegen, die Seite nennt den Grund dauerhaft. Fehlt, wenn der Lauf nicht an einem Fehler endete.
   */
  blockedBy?: { code: string; status: number; message: string };
  /** Scans einer anderen Person: Sie bleiben liegen und gehen nur unter deren Konto hinaus (fehlt, wenn keine). */
  held?: number;
}

/**
 * Antworten, die genau zu diesem einen Scan gehören (Etikett unbekannt, Artikel gelöscht, Verkäufer nicht freigegeben,
 * Artikel schon weiterverarbeitet): Wiederholen ändert nichts. Der Scan wird mit Hinweis aus der Warteschlange genommen.
 * Alles andere, was mit dem Scan nichts zu tun hat (kein Netz, 401, 403, 409 „Annahme nicht geöffnet“, 429, 5xx, unerwartete
 * Fehler, siehe `isSystemFailure`): Der Lauf bricht ab (`blockedBy`), der Scan bleibt unverändert liegen. Sonstige Absagen zu
 * diesem Scan zählen als Fehlversuch (`MAX_SCAN_ATTEMPTS`), danach wird er mit Hinweis aus der Warteschlange genommen.
 */
const SCAN_REJECTIONS: ReadonlySet<string> = new Set([
  'INVALID_CODE',
  'UNKNOWN_CODE',
  'ITEM_DELETED',
  'NOT_APPROVED',
  'ITEM_STATE',
]);

export const isScanRejection = (error: unknown): error is ApiClientError =>
  error instanceof ApiClientError &&
  SCAN_REJECTIONS.has(error.code) &&
  (error.status === 400 || error.status === 404 || error.status === 409);

/**
 * Überträgt die wartenden Annahme-Scans (U15c). Die Regeln sind die der Rückgabe-Station (`flushOutbox`): Gesendet wird nur
 * unter dem Konto der Person, die den Scan erfasst hat. Die Person wird vor JEDEM Scan neu gelesen; wechselt sie mitten im Lauf,
 * ist die Sitzung weg oder bricht `options.signal` ab, geht kein weiterer Scan hinaus. Scans einer anderen Person bleiben
 * unberührt liegen. Der Server-Scan ist idempotent („Schon erfasst“): Ein wiederholter Scan (Antwort ging verloren) schadet nicht.
 */
export async function flushIntake(
  store: StationStore,
  api: IntakeScanApi,
  eventId: string,
  person: PersonSource = null,
  options: IntakeFlushOptions = {},
): Promise<IntakeFlushSummary> {
  const read = typeof person === 'function' ? person : () => person;
  const live = typeof person === 'function';
  const start = read();
  const stillValid = (): boolean => read() === start && (!live || getAccessToken() !== null);
  const guard: ScanGuard = {
    ...(options.signal === undefined ? {} : { signal: options.signal }),
    stillValid,
  };
  const canGo = (): boolean => options.signal?.aborted !== true && stillValid();
  const summary = { sent: 0, alreadyReceived: 0, rejected: 0 };
  let blockedBy: IntakeFlushSummary['blockedBy'];
  const scans = await store.all();
  const held = scans.filter((scan) => isOthers(scan, start)).length;
  for (const scan of scans) {
    if (isOthers(scan, start)) continue;
    if (!canGo()) break;
    try {
      const result = await api.intakeScan(eventId, scan.code, guard);
      await store.markDone(scan.id);
      summary.sent += 1;
      if (result.alreadyReceived) summary.alreadyReceived += 1;
      options.onResult?.(scan, result);
    } catch (error) {
      if (isScanRejection(error)) {
        // Der Hinweis wird zuerst gespeichert, dann der Scan entfernt: Nichts verschwindet ohne Grund
        await addRejected(store, {
          code: scan.code,
          message: error.message,
          userId: scan.userId ?? null,
          at: Date.now(),
        });
        await store.markDone(scan.id);
        summary.rejected += 1;
      } else if (isSystemFailure(error)) {
        // Hat mit diesem Scan nichts zu tun: kein Fehlversuch, der Scan bleibt liegen, die Seite nennt den Grund
        blockedBy =
          error instanceof ApiClientError
            ? { code: error.code, status: error.status, message: messageFor(error) }
            : { code: 'UNEXPECTED', status: 0, message: messageFor(error) };
        break;
      } else {
        // Sonstige Absage zu genau diesem Scan (z. B. 400/404 ohne bekannten Grund): zählt wie bei der Rückgabe-Station als
        // Fehlversuch; an der Grenze kommt der Scan MIT Hinweis aus der Warteschlange (nie still)
        if ((await store.markAttempt(scan.id)) === 'dropped') {
          await addRejected(store, {
            code: scan.code,
            message: `Nach mehreren Fehlversuchen nicht übertragen: ${error instanceof Error ? error.message : 'Fehler'}`,
            userId: scan.userId ?? null,
            at: Date.now(),
          });
          summary.rejected += 1;
        }
      }
    }
  }
  return {
    ...summary,
    remaining: await store.pending(),
    ...(blockedBy === undefined ? {} : { blockedBy }),
    ...(held > 0 ? { held } : {}),
  };
}
